"use client";
import { useRouter } from "next/navigation";
import { useState } from "react";

interface DailyRecord { date: string; spend: number; }

interface CostsData {
  today: { date: string; spend: number };
  weekly: number;
  monthly: number;
  total: number;
  limit: number | null;
  limitRemaining: number | null;
  history: DailyRecord[];
}

interface Props {
  initialData: CostsData | { error: string };
}

function fmt(usd: number): string {
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return `$${usd.toFixed(6)}`;
  return `$${usd.toFixed(4)}`;
}

function fmtDate(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function CostsClient({ initialData }: Props) {
  const router = useRouter();
  const [refreshing, setRefreshing] = useState(false);

  async function refresh() {
    setRefreshing(true);
    router.refresh();
    // Give the server a moment to re-render
    setTimeout(() => setRefreshing(false), 1500);
  }

  if ("error" in initialData) {
    return (
      <div className="max-w-3xl mx-auto px-12 py-10">
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-5 py-4 text-sm">
          {initialData.error}
        </div>
      </div>
    );
  }

  const data = initialData;
  const maxSpend = Math.max(...data.history.map((d) => d.spend), 0.0001);

  return (
    <div className="max-w-3xl mx-auto px-12 py-10">
      {/* Header */}
      <div className="flex items-center justify-between pb-6 mb-8 border-b border-[#e8e2d8]">
        <div className="flex items-baseline gap-4">
          <h1 className="text-[28px] font-serif font-normal text-[#2c2416] tracking-tight">
            Costs
          </h1>
          <span className="text-sm text-[#a0906e] italic">OpenRouter · Claude Sonnet 4-6</span>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="text-[12px] text-[#b0a090] hover:text-[#b5622a] transition-colors disabled:opacity-50"
        >
          {refreshing ? "Refreshing…" : "↺ Refresh"}
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-10">
        {[
          { label: "Today",      value: fmt(data.today.spend), sub: fmtDate(data.today.date) },
          { label: "This Week",  value: fmt(data.weekly),      sub: "rolling 7 days" },
          { label: "This Month", value: fmt(data.monthly),     sub: "rolling 30 days" },
        ].map(({ label, value, sub }) => (
          <div key={label} className="bg-white border border-[#e8e2d8] rounded-lg p-5">
            <div className="text-[11px] tracking-[0.1em] uppercase text-[#b0a090] font-semibold mb-2">{label}</div>
            <div className="font-serif text-[22px] text-[#2c2416] mb-1">{value}</div>
            <div className="text-[11px] text-[#b0a090] italic">{sub}</div>
          </div>
        ))}
      </div>

      {/* Balance bar */}
      <div className="bg-[#fdf7f0] border border-[#e8c4a0] rounded-lg px-6 py-4 mb-10 flex items-center justify-between">
        <div>
          <div className="text-[11px] tracking-[0.1em] uppercase text-[#b0a090] font-semibold mb-1">Account Balance</div>
          <div className="text-[14px] text-[#6b5c48]">
            {data.limitRemaining != null
              ? <><span className="font-semibold text-[#2c2416]">{fmt(data.limitRemaining)}</span> remaining{data.limit && <span className="text-[#b0a090]"> of {fmt(data.limit)} limit</span>}</>
              : <span className="text-[#b0a090] italic">No spending limit set</span>
            }
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] tracking-[0.1em] uppercase text-[#b0a090] font-semibold mb-1">All-Time Spend</div>
          <div className="font-semibold text-[#2c2416] text-[14px]">{fmt(data.total)}</div>
        </div>
      </div>

      {/* 14-day bar chart */}
      <div>
        <div className="text-[11px] tracking-[0.1em] uppercase text-[#b0a090] font-semibold mb-5">
          Last 14 Days
        </div>
        <div className="bg-white border border-[#e8e2d8] rounded-lg p-6">
          <div className="flex items-end gap-1.5 h-28">
            {data.history.map((day) => {
              const isToday = day.date === data.today.date;
              const heightPct = (day.spend / maxSpend) * 100;
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center group relative">
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[#2c2416] text-white text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    {fmtDate(day.date)}: {fmt(day.spend)}
                  </div>
                  {/* Bar */}
                  <div className="w-full flex items-end" style={{ height: "100px" }}>
                    <div
                      className={`w-full rounded-t transition-all ${isToday ? "bg-[#b5622a]" : "bg-[#e4ddd0] group-hover:bg-[#c4b8a8]"}`}
                      style={{ height: `${Math.max(heightPct, day.spend > 0 ? 4 : 1)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {/* X-axis */}
          <div className="flex gap-1.5 mt-2">
            {data.history.map((day, i) => (
              <div key={day.date} className="flex-1 text-center">
                {i % 2 === 0 && (
                  <span className={`text-[9px] ${day.date === data.today.date ? "text-[#b5622a] font-semibold" : "text-[#c0b8b0]"}`}>
                    {fmtDate(day.date).split(" ")[1]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
